import { FLIGHT } from '../config';

/** Per-frame flight intents, read once per frame by the game. */
export type FlightIntent = {
  pitch: number; // -1..1 (positive = nose up)
  yaw: number; // positive = nose right
  roll: number; // positive = roll right
  throttleDelta: number; // -1..1 continuous
  strafeUp: number; // -1..1
  boost: boolean;
  fire: boolean;
  fullStop: boolean;
};

type Action =
  | 'target' | 'autopilot' | 'camera' | 'map' | 'players' | 'chat' | 'pause' | 'follow' | 'mute' | 'warp';

/**
 * Keyboard + pointer-locked mouse (virtual stick) + gamepad.
 * One-shot actions are queued and drained by the game each frame.
 */
export class Input {
  private readonly keys = new Set<string>();
  readonly stick = { x: 0, y: 0 };
  private mouseMovedThisFrame = false;
  private mouseDown = false;
  private readonly actions: Action[] = [];
  enabled = true;
  private lastPadButtons: boolean[] = [];

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => this.keys.clear());
    canvas.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', () => (this.mouseDown = false));
    document.addEventListener('mousemove', this.onMouseMove);
  }

  get pointerLocked(): boolean {
    return document.pointerLockElement === this.canvas;
  }

  requestPointerLock(): void {
    if (this.pointerLocked) return;
    try {
      const p = this.canvas.requestPointerLock?.() as unknown as Promise<void> | undefined;
      p?.catch?.(() => {});
    } catch {
      /* not allowed here (iframe, automation, or no user gesture) */
    }
  }

  private readonly onKeyDown = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
    if (!this.enabled && e.code !== 'Escape') return;
    if (e.code === 'Tab') e.preventDefault();
    if (!e.repeat) {
      const map: Record<string, Action> = {
        KeyT: 'target', KeyJ: 'autopilot', KeyC: 'camera', KeyM: 'map', Tab: 'players',
        Enter: 'chat', Escape: 'pause', KeyG: 'follow', KeyN: 'mute', KeyH: 'warp',
      };
      const a = map[e.code];
      if (a) this.actions.push(a);
    }
    this.keys.add(e.code);
  };

  private readonly onKeyUp = (e: KeyboardEvent) => {
    this.keys.delete(e.code);
  };

  private readonly onMouseDown = (e: MouseEvent) => {
    if (!this.enabled) return;
    if (!this.pointerLocked) {
      this.requestPointerLock();
      return;
    }
    if (e.button === 0) this.mouseDown = true;
  };

  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.pointerLocked || !this.enabled) return;
    this.stick.x += e.movementX * FLIGHT.mouseSensitivity;
    this.stick.y += e.movementY * FLIGHT.mouseSensitivity;
    const len = Math.hypot(this.stick.x, this.stick.y);
    if (len > 1) {
      this.stick.x /= len;
      this.stick.y /= len;
    }
    this.mouseMovedThisFrame = true;
  };

  drainActions(): Action[] {
    const pad = this.readPadButtons();
    const out = this.actions.splice(0);
    return out.concat(pad);
  }

  isHeld(code: string): boolean {
    return this.keys.has(code);
  }

  read(dt: number, out: FlightIntent): FlightIntent {
    // Virtual stick recentres slowly when the mouse rests.
    if (!this.mouseMovedThisFrame) {
      const k = Math.exp(-FLIGHT.stickRecenter * dt);
      this.stick.x *= k;
      this.stick.y *= k;
    }
    this.mouseMovedThisFrame = false;
    const dead = (v: number) => (Math.abs(v) < 0.04 ? 0 : v);
    const k = (c: string) => (this.enabled && this.keys.has(c) ? 1 : 0);
    out.pitch = -dead(this.stick.y) + (k('ArrowDown') - k('ArrowUp'));
    out.yaw = dead(this.stick.x) + (k('ArrowRight') - k('ArrowLeft'));
    out.roll = k('KeyD') - k('KeyA');
    out.throttleDelta = k('KeyW') - k('KeyS');
    out.strafeUp = k('KeyR') - k('KeyF');
    out.boost = !!(k('ShiftLeft') || k('ShiftRight'));
    out.fire = this.enabled && (this.mouseDown || this.keys.has('Space'));
    out.fullStop = !!k('KeyX');

    const pad = navigator.getGamepads?.().find((p) => p && p.connected);
    if (pad) {
      const ax = (i: number) => dead(pad.axes[i] ?? 0);
      const btn = (i: number) => pad.buttons[i]?.value ?? 0;
      out.pitch += -ax(3);
      out.yaw += ax(2);
      out.roll += ax(0);
      out.throttleDelta += btn(7) - btn(6);
      out.strafeUp += btn(5) - btn(4);
      out.boost ||= btn(10) > 0.5;
      out.fire ||= btn(0) > 0.5;
      out.fullStop ||= btn(1) > 0.5;
    }
    out.pitch = Math.max(-1, Math.min(1, out.pitch));
    out.yaw = Math.max(-1, Math.min(1, out.yaw));
    out.roll = Math.max(-1, Math.min(1, out.roll));
    return out;
  }

  private readPadButtons(): Action[] {
    const pad = navigator.getGamepads?.().find((p) => p && p.connected);
    if (!pad) return [];
    const map: [number, Action][] = [[3, 'target'], [2, 'autopilot'], [11, 'camera'], [8, 'map'], [9, 'pause']];
    const out: Action[] = [];
    for (const [i, a] of map) {
      const down = (pad.buttons[i]?.value ?? 0) > 0.5;
      if (down && !this.lastPadButtons[i]) out.push(a);
      this.lastPadButtons[i] = down;
    }
    return out;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mousemove', this.onMouseMove);
  }
}
